import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { User, Bot, UserRound, MessageSquare, Circle } from 'lucide-react';

interface Conversation {
  id: string;
  lead_phone: string;
  lead_name: string | null;
  control_mode: 'agent' | 'lorena' | 'agent_observing';
  started_at: string;
  ended_at: string | null;
  outcome: string | null;
  last_lead_message_at: string | null;
  last_agent_message_at: string | null;
  last_lorena_message_at: string | null;
}

interface Message {
  id: string;
  sender: 'agent' | 'lorena' | 'lead' | 'system';
  content: string;
  created_at: string;
  media_type: string | null;
  transcription: string | null;
}

interface Profile {
  score: number;
  confidence: number;
  factors_positive: string[];
  factors_negative: string[];
  extracted_data: Record<string, string | number>;
}

export default function ConversationsTab({ sellerId }: { sellerId: string }) {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedId, setSelectedId] = useState<string>('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<'active' | 'ended' | 'all'>('active');

  useEffect(() => {
    void loadConversations();
  }, [sellerId, statusFilter]);

  useEffect(() => {
    if (selectedId) void loadMessages(selectedId);
    else {
      setMessages([]);
      setProfile(null);
    }
  }, [selectedId]);

  async function loadConversations() {
    setLoading(true);
    let query = supabase
      .from('ai_conversations')
      .select('*')
      .eq('seller_id', sellerId)
      .order('started_at', { ascending: false })
      .limit(100);
    if (statusFilter === 'active') query = query.is('ended_at', null);
    if (statusFilter === 'ended') query = query.not('ended_at', 'is', null);
    const { data } = await query;
    const list = (data ?? []) as Conversation[];
    setConversations(list);
    if (list.length > 0 && !list.find((c) => c.id === selectedId)) setSelectedId(list[0].id);
    else if (list.length === 0) setSelectedId('');
    setLoading(false);
  }

  async function loadMessages(conversationId: string) {
    const { data: msgs } = await supabase
      .from('ai_messages')
      .select('*')
      .eq('conversation_id', conversationId)
      .order('created_at');
    setMessages((msgs ?? []) as Message[]);

    const { data: prof } = await supabase
      .from('ai_lead_profiles')
      .select('*')
      .eq('conversation_id', conversationId)
      .maybeSingle();
    if (prof) {
      setProfile({
        score: (prof as any).score,
        confidence: (prof as any).confidence,
        factors_positive: (prof as any).factors_positive ?? [],
        factors_negative: (prof as any).factors_negative ?? [],
        extracted_data: (prof as any).extracted_data ?? {},
      });
    } else {
      setProfile(null);
    }
  }

  async function handoffToLorena(conversationId: string) {
    await supabase
      .from('ai_conversations')
      .update({ control_mode: 'lorena', last_lorena_message_at: new Date().toISOString() })
      .eq('id', conversationId);
    void loadConversations();
  }

  async function returnToAgent(conversationId: string) {
    await supabase.from('ai_conversations').update({ control_mode: 'agent' }).eq('id', conversationId);
    void loadConversations();
  }

  if (loading) return <div className="text-sm text-slate-500">Carregando...</div>;

  const selected = conversations.find((c) => c.id === selectedId) ?? null;

  return (
    <div className="grid gap-4 lg:grid-cols-[320px_1fr_280px]">
      <div className="rounded-xl border border-slate-200 bg-white">
        <div className="border-b border-slate-200 p-3">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Conversas</div>
          <div className="flex gap-1 rounded-lg bg-slate-100 p-1 text-xs">
            {(['active', 'ended', 'all'] as const).map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={
                  'flex-1 rounded-md px-2 py-1 font-medium transition ' +
                  (statusFilter === s ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900')
                }
              >
                {s === 'active' ? 'Ativas' : s === 'ended' ? 'Encerradas' : 'Todas'}
              </button>
            ))}
          </div>
        </div>
        <div className="max-h-[540px] overflow-y-auto">
          {conversations.length === 0 && (
            <div className="p-6 text-center text-xs text-slate-400">Nenhuma conversa.</div>
          )}
          {conversations.map((c) => (
            <button
              key={c.id}
              onClick={() => setSelectedId(c.id)}
              className={
                'w-full border-b border-slate-100 p-3 text-left transition ' +
                (selectedId === c.id ? 'bg-emerald-50' : 'hover:bg-slate-50')
              }
            >
              <div className="flex items-center justify-between">
                <span className="truncate text-sm font-medium text-slate-900">
                  {c.lead_name || c.lead_phone}
                </span>
                <ControlBadge mode={c.control_mode} />
              </div>
              <div className="mt-1 text-xs text-slate-500">{new Date(c.started_at).toLocaleDateString('pt-BR')}</div>
              {c.outcome && c.outcome !== 'open' && (
                <div className="mt-1 text-xs text-slate-400">Resultado: {c.outcome}</div>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="flex min-h-[500px] flex-col rounded-xl border border-slate-200 bg-white">
        {!selected && (
          <div className="flex flex-1 items-center justify-center text-sm text-slate-400">
            <MessageSquare className="mr-2 h-5 w-5" />
            Selecione uma conversa
          </div>
        )}
        {selected && (
          <>
            <div className="flex items-center justify-between border-b border-slate-200 p-3">
              <div>
                <div className="text-sm font-semibold text-slate-900">
                  {selected.lead_name || selected.lead_phone}
                </div>
                <div className="text-xs text-slate-500">
                  <Circle className={
                    'mr-1 inline h-2 w-2 fill-current ' +
                    (selected.control_mode === 'agent'
                      ? 'text-emerald-500'
                      : selected.control_mode === 'lorena'
                      ? 'text-amber-500'
                      : 'text-blue-500')
                  } />
                  {selected.control_mode === 'agent'
                    ? 'Agente no controle'
                    : selected.control_mode === 'lorena'
                    ? 'Lorena no controle'
                    : 'Agente observando'}
                </div>
              </div>
              <div className="flex gap-2">
                {selected.control_mode !== 'lorena' && (
                  <button
                    onClick={() => handoffToLorena(selected.id)}
                    className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-700 hover:bg-amber-100"
                  >
                    Passar para Lorena
                  </button>
                )}
                {selected.control_mode !== 'agent' && (
                  <button
                    onClick={() => returnToAgent(selected.id)}
                    className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-100"
                  >
                    Devolver ao agente
                  </button>
                )}
              </div>
            </div>
            <div className="flex-1 space-y-3 overflow-y-auto p-4">
              {messages.length === 0 ? (
                <div className="py-8 text-center text-sm text-slate-400">Sem mensagens ainda.</div>
              ) : (
                messages.map((m) => <MessageBubble key={m.id} message={m} />)
              )}
            </div>
          </>
        )}
      </div>

      <div className="space-y-4">
        {profile && (
          <>
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Score do lead
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-semibold text-slate-900">{profile.score}</span>
                <span className="text-sm text-slate-500">/100</span>
              </div>
              <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-100">
                <div
                  className={
                    'h-full transition-all ' +
                    (profile.score >= 70
                      ? 'bg-emerald-500'
                      : profile.score >= 40
                      ? 'bg-amber-500'
                      : 'bg-red-500')
                  }
                  style={{ width: profile.score + '%' }}
                />
              </div>
              <div className="mt-2 text-xs text-slate-500">Confianca: {profile.confidence}%</div>
            </div>

            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
              <div className="mb-2 text-xs font-semibold text-emerald-700">A favor</div>
              {profile.factors_positive.length === 0 ? (
                <div className="text-xs text-emerald-600 opacity-70">Sem sinais ainda.</div>
              ) : (
                <ul className="space-y-1 text-xs text-emerald-800">
                  {profile.factors_positive.map((f, i) => (
                    <li key={i}>• {f}</li>
                  ))}
                </ul>
              )}
            </div>

            <div className="rounded-xl border border-red-200 bg-red-50 p-4">
              <div className="mb-2 text-xs font-semibold text-red-700">Contra</div>
              {profile.factors_negative.length === 0 ? (
                <div className="text-xs text-red-600 opacity-70">Sem sinais ainda.</div>
              ) : (
                <ul className="space-y-1 text-xs text-red-800">
                  {profile.factors_negative.map((f, i) => (
                    <li key={i}>• {f}</li>
                  ))}
                </ul>
              )}
            </div>

            {Object.keys(profile.extracted_data).length > 0 && (
              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Dados capturados
                </div>
                <dl className="space-y-1 text-xs">
                  {Object.entries(profile.extracted_data).map(([k, v]) => (
                    <div key={k} className="flex justify-between gap-2">
                      <dt className="text-slate-500">{k}</dt>
                      <dd className="text-right font-medium text-slate-900">{String(v)}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}
          </>
        )}
        {!profile && selected && (
          <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-4 text-center text-xs text-slate-500">
            Sem perfil ainda. O agente vai preenchendo enquanto conversa.
          </div>
        )}
      </div>
    </div>
  );
}

function ControlBadge({ mode }: { mode: 'agent' | 'lorena' | 'agent_observing' }) {
  const config = {
    agent: { label: 'Agente', className: 'bg-emerald-100 text-emerald-700', Icon: Bot },
    lorena: { label: 'Lorena', className: 'bg-amber-100 text-amber-700', Icon: UserRound },
    agent_observing: { label: 'Observa', className: 'bg-blue-100 text-blue-700', Icon: Bot },
  } as const;
  const { label, className, Icon } = config[mode];
  return (
    <span className={'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ' + className}>
      <Icon className="h-2.5 w-2.5" />
      {label}
    </span>
  );
}

function MessageBubble({ message }: { message: Message }) {
  const isLead = message.sender === 'lead';
  const isAgent = message.sender === 'agent';
  const isSystem = message.sender === 'system';

  if (isSystem) {
    return (
      <div className="text-center text-xs italic text-slate-400">{message.content}</div>
    );
  }

  return (
    <div className={'flex ' + (isLead ? 'justify-start' : 'justify-end')}>
      <div
        className={
          'max-w-[85%] rounded-2xl px-3 py-2 text-sm ' +
          (isLead
            ? 'bg-slate-100 text-slate-900'
            : isAgent
            ? 'bg-emerald-100 text-emerald-900'
            : 'bg-amber-100 text-amber-900')
        }
      >
        <div className="mb-0.5 flex items-center gap-1 text-[10px] uppercase tracking-wide opacity-60">
          {isLead ? <User className="h-2.5 w-2.5" /> : isAgent ? <Bot className="h-2.5 w-2.5" /> : <UserRound className="h-2.5 w-2.5" />}
          {isLead ? 'Lead' : isAgent ? 'Agente' : 'Lorena'}
          <span className="ml-auto opacity-60">
            {new Date(message.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
          </span>
        </div>
        <div className="whitespace-pre-wrap">{message.content}</div>
        {message.transcription && (
          <div className="mt-1 rounded border-l-2 border-current pl-2 text-[11px] italic opacity-70">
            (audio): {message.transcription}
          </div>
        )}
      </div>
    </div>
  );
}
